import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { notificationAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useNotificationCount from '../../hooks/useNotificationCount';
import { formatRelativeTime } from '../../utils/dateTime';

const TYPE_COLORS = {
  DEAL_REQUEST: '#667eea',
  DEAL_APPROVED: '#10b981',
  DEAL_REJECTED: '#ef4444',
  ORDER_SUBMITTED: '#667eea',
  ORDER_STATUS: '#f59e0b',
  REFUND_SUBMITTED: '#10b981',
  REFUND_REMINDER: '#f59e0b',
  REFUND_PENALTY_50: '#ef4444',
  REFUND_PENALTY_100: '#dc2626',
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { hasRole, isAuthenticated } = useAuth();
  const { unreadCount, refreshUnreadCount } = useNotificationCount();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const panelRef = useRef(null);

  const getNotificationsPath = () => {
    if (hasRole('admin')) return '/admin/notifications';
    if (hasRole('mediator')) return '/mediator/notifications';
    if (hasRole('buyer')) return '/buyer/notifications';
    return '/notifications';
  };

  useEffect(() => {
    const updateViewport = () => setIsMobile(window.innerWidth < 768);
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = isMobile ? 'hidden' : '';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, isMobile]);

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getAll();
      const list = res.data?.data || res.data || [];
      setNotifications(Array.isArray(list) ? list.slice(0, 8) : []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchRecent();
    }
  }, [open, fetchRecent]);

  const handleNotificationClick = async (notif) => {
    if (!notif.readStatus) {
      try {
        await notificationAPI.markAsRead(notif.id);
        refreshUnreadCount();
      } catch {
        // continue navigation
      }
    }
    setOpen(false);
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
    } else {
      navigate(getNotificationsPath());
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      refreshUnreadCount();
      fetchRecent();
    } catch {
      // ignore
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div ref={panelRef} className="notification-bell-root" style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        aria-label="Notifications"
        aria-expanded={open}
        style={{
          position: 'relative',
          background: open ? '#f1f5f9' : 'transparent',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#64748b',
          minWidth: '40px',
          minHeight: '40px',
        }}
      >
        <Icon name="bell" size={18} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '18px',
              height: '18px',
              padding: '0 4px',
              background: '#ef4444',
              color: 'white',
              borderRadius: '9px',
              fontSize: '10px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {isMobile && (
            <button
              type="button"
              aria-label="Close notifications"
              onClick={() => setOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15, 23, 42, 0.45)',
                border: 'none',
                zIndex: 120,
              }}
            />
          )}

          <div
            className={`notification-panel ${isMobile ? 'notification-panel-mobile' : ''}`}
            style={{
              position: isMobile ? 'fixed' : 'absolute',
              top: isMobile ? 'auto' : 'calc(100% + 8px)',
              bottom: isMobile ? 0 : 'auto',
              right: isMobile ? 0 : 0,
              left: isMobile ? 0 : 'auto',
              width: isMobile ? '100%' : '360px',
              maxWidth: isMobile ? '100%' : 'min(360px, calc(100vw - 24px))',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: isMobile ? '16px 16px 0 0' : '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
              zIndex: 121,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
                Notifications
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllRead}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#667eea',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Mark all read
                  </button>
                )}
                {isMobile && (
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    style={{
                      background: '#f1f5f9',
                      border: 'none',
                      borderRadius: '8px',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon name="x" size={16} />
                  </button>
                )}
              </div>
            </div>

            <div style={{ maxHeight: isMobile ? '55vh' : '360px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {loading ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                  Loading...
                </div>
              ) : notifications.length === 0 ? (
                <div style={{ padding: '32px 24px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                  <Icon name="bell" size={32} />
                  <p style={{ margin: '12px 0 0' }}>No notifications yet</p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <button
                    key={notif.id}
                    type="button"
                    onClick={() => handleNotificationClick(notif)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 16px',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      background: notif.readStatus ? '#ffffff' : '#f8faff',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        marginTop: '6px',
                        flexShrink: 0,
                        background: notif.readStatus ? 'transparent' : (TYPE_COLORS[notif.type] || '#667eea'),
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: notif.readStatus ? 500 : 700, fontSize: '13px', color: '#1e293b' }}>
                        {notif.title}
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#64748b',
                          marginTop: '2px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {notif.message}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                        {formatRelativeTime(notif.createdAt)}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(getNotificationsPath());
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  padding: '8px 0',
                }}
              >
                View all notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
